// The slot names' own module, not the organizations folder's index, which would pull its chooser into the main bundle.
import { organizationDetailsSlot } from '@/features/site-management/organizations/organization'
import { activityAddSlot } from '@/features/catalog/activities/add-slot'
import { ltiToolPickerSlot } from '@/features/catalog/activities/lti/lti-link-activity'
import { assessmentDetailsSlot } from '@/features/catalog/assessments/paths'
import { courseCatalogSlots } from '@/features/catalog/course-catalog/paths'
import { surveyDetailsSlot } from '@/features/catalog/surveys/paths'
import { classDetailsSlot } from '@/features/classes/class-form/class'
import { classSlots } from '@/features/classes/slots'
import { courseAssignmentActivitiesSlot } from '@/features/entitlements/course-assignments/paths'
import { enrollmentActivitiesSlot } from '@/features/entitlements/enrollments/paths'
import { studentDashboardSlots } from '@/features/entitlements/my-training/paths'
import { organizationFormSectionsSlot } from '@/features/site-management/organizations/paths'
import { detailsSlots } from '@/shared/details'
import { column, shellSlots, type Feature } from '@/shared/registry'
import { FileSpreadsheet } from '@/shared/ui/icons'
import { myDataSlot } from '@/shared/search/my-data'
import { Activity, ClipboardCheck, ClipboardList, Download, FlaskConical } from '@/shared/ui/icons'

import type { ClassProgressCellValue } from './class-progress/class-progress'

import {
  connectionRoute,
  connectionsPath,
  createConnectionPath,
  editConnectionRoute,
  readPermission as labOnDemandRead,
  roleMappingsRoute,
} from './lab-on-demand/paths'
import {
  createLtiToolPath,
  deepLinkReturnPath,
  editLtiToolRoute,
  ltiPlatformPath,
  ltiToolRoute,
  ltiToolsPath,
  readLtiToolsPermission,
  writeLtiToolsPermission,
} from './lti/paths'
import { classExamLabInstancesPath, classLabInstancesPath, labInstanceRoute, labInstancesPath, readLabInstancesPermission } from './lab-instances/paths'
import { labMonitorPath, labMonitorRoute, writeLabInstancesPermission } from './lab-instances/paths'
import { labRoute } from './labs/paths'
import { launchesPath, readLaunchesPermission } from './launches/paths'
import { videoPath } from './links/paths'
import { myDataApi } from './my-data/paths'
import { scormAttemptRoute, scormPlayerRoute, scormReviewPath } from './scorm/scorm'
import { cmi5LaunchRoute } from './cmi5/cmi5'
import { assessmentReviewPath, assessmentRoute } from './assessments/assessment'
import { assessmentResponseRoute, assessmentResponsesFor, assessmentResponsesPath, readAssessmentResponsesPermission } from './assessments/assessment-responses'
import { classSurveyRoute, grantedSurveyRoute } from './surveys/survey'
import {
  classSurveyResultsRoute,
  classTextAnswersRoute,
  readRespondentsPermission,
  readSurveyResponsesPermission,
  resultsPath,
  surveyResponseRoute,
  surveyResultsRoute,
  surveyTextAnswersRoute,
} from './surveys/survey-results'

/**
 * Launch: starts every kind of activity and records progress. Its routes, nav items and
 * contributions to other areas' pages and slots register here; each activity type's launch
 * button registers in `launch-types.ts`.
 */
export const feature: Feature = {
  id: 'launch',
  routes: [
    // Lab on Demand connections (LCH-31).
    {
      path: connectionsPath,
      titleId: 'launch.labOnDemand.find.title',
      page: () => import('./lab-on-demand/FindConnectionsPage').then((module) => module.FindConnectionsPage),
    },
    {
      path: createConnectionPath,
      titleId: 'launch.labOnDemand.create.title',
      page: () => import('./lab-on-demand/ConnectionFormPages').then((module) => module.CreateConnectionPage),
    },
    {
      path: connectionRoute,
      titleId: 'launch.labOnDemand.details.title',
      page: () => import('./lab-on-demand/ConnectionDetailsPage').then((module) => module.ConnectionDetailsPage),
    },
    {
      path: editConnectionRoute,
      titleId: 'launch.labOnDemand.edit.title',
      page: () => import('./lab-on-demand/ConnectionFormPages').then((module) => module.EditConnectionPage),
    },
    // A connection's role mappings (LCH-10).
    {
      path: roleMappingsRoute,
      titleId: 'launch.labOnDemand.roleMappings.title',
      page: () => import('./lab-on-demand/RoleMappingsPage').then((module) => module.RoleMappingsPage),
    },
    // LTI tools and the platform's URLs (LCH-15).
    {
      path: ltiToolsPath,
      titleId: 'launch.lti.find.title',
      page: () => import('./lti/FindLtiToolsPage').then((module) => module.FindLtiToolsPage),
    },
    {
      path: createLtiToolPath,
      titleId: 'launch.lti.create.title',
      page: () => import('./lti/LtiToolFormPages').then((module) => module.CreateLtiToolPage),
    },
    {
      path: ltiToolRoute,
      titleId: 'launch.lti.details.title',
      page: () => import('./lti/LtiToolDetailsPage').then((module) => module.LtiToolDetailsPage),
    },
    {
      path: editLtiToolRoute,
      titleId: 'launch.lti.edit.title',
      page: () => import('./lti/LtiToolFormPages').then((module) => module.EditLtiToolPage),
    },
    {
      path: ltiPlatformPath,
      titleId: 'launch.lti.platform.title',
      page: () => import('./lti/LtiPlatformPage').then((module) => module.LtiPlatformPage),
    },
    // Where Add from Tool's window comes back to (LCH-18): it only hands on what its address says, so it needs no sign-in.
    {
      path: deepLinkReturnPath,
      titleId: 'launch.lti.deepLink.title',
      page: () => import('./lti/DeepLinkReturnPage').then((module) => module.DeepLinkReturnPage),
      public: true,
    },
    // The video player (LCH-04).
    {
      path: videoPath,
      titleId: 'catalog.activities.type.video',
      page: () => import('./links/VideoPage').then((module) => module.VideoPage),
    },
    // The learner's lab page (LCH-06).
    {
      path: labRoute,
      titleId: 'launch.labs.eyebrow',
      page: () => import('./labs/LabPage').then((module) => module.LabPage),
    },
    // Find Lab Instances and Lab Instance Details (LCH-08).
    {
      path: labInstancesPath,
      titleId: 'launch.labInstances.find.title',
      page: () => import('./lab-instances/FindLabInstancesPage').then((module) => module.FindLabInstancesPage),
    },
    {
      path: labInstanceRoute,
      titleId: 'launch.labInstances.details.eyebrow',
      page: () => import('./lab-instances/LabInstanceDetailsPage').then((module) => module.LabInstanceDetailsPage),
    },
    // Monitor Labs, from Class Details (LCH-09).
    {
      path: labMonitorRoute,
      titleId: 'launch.labInstances.monitor.title',
      page: () => import('./lab-instances/LabMonitorPage').then((module) => module.LabMonitorPage),
    },
    // The SCORM player, its review mode, and attempt details for admins (LCH-12).
    {
      path: scormPlayerRoute,
      titleId: 'catalog.activities.type.scorm',
      page: () => import('./scorm/ScormPlayerPage').then((module) => module.ScormPlayerPage),
    },
    {
      path: scormReviewPath,
      titleId: 'catalog.activities.type.scorm',
      page: () => import('./scorm/ScormPlayerPage').then((module) => module.ScormReviewPage),
    },
    {
      path: scormAttemptRoute,
      titleId: 'launch.scorm.attempt.eyebrow',
      page: () => import('./scorm/ScormAttemptPage').then((module) => module.ScormAttemptPage),
    },
    // The cmi5 launch page (LCH-13).
    {
      path: cmi5LaunchRoute,
      titleId: 'catalog.activities.type.cmi5',
      page: () => import('./cmi5/Cmi5LaunchPage').then((module) => module.Cmi5LaunchPage),
    },
    // Taking an assessment, and reviewing the learner's own answers (LCH-19).
    {
      path: assessmentRoute,
      titleId: 'launch.assessments.eyebrow',
      page: () => import('./assessments/AssessmentPage').then((module) => module.AssessmentPage),
    },
    {
      path: assessmentReviewPath,
      titleId: 'launch.assessments.review.eyebrow',
      page: () => import('./assessments/AssessmentReviewPage').then((module) => module.AssessmentReviewPage),
    },    // Answering a class's survey slot, the page Notifications' [SurveyLink] opens, or a survey given on its own (LCH-21).
    {
      path: classSurveyRoute,
      titleId: 'launch.surveys.eyebrow',
      page: () => import('./surveys/SurveyPage').then((module) => module.ClassSurveyPage),
    },
    {
      path: grantedSurveyRoute,
      titleId: 'launch.surveys.eyebrow',
      page: () => import('./surveys/SurveyPage').then((module) => module.GrantedSurveyPage),
    },
    // Find Launches (LCH-24).
    {
      path: launchesPath,
      titleId: 'launch.launches.find.title',
      page: () => import('./launches/FindLaunchesPage').then((module) => module.FindLaunchesPage),
    },
    // Find Assessment Responses and response details (LCH-20).
    {
      path: assessmentResponsesPath,
      titleId: 'launch.assessmentResponses.find.title',
      page: () => import('./assessments/FindAssessmentResponsesPage').then((module) => module.FindAssessmentResponsesPage),
    },
    {
      path: assessmentResponseRoute,
      titleId: 'launch.assessmentResponses.details.eyebrow',
      page: () => import('./assessments/AssessmentResponseDetailsPage').then((module) => module.AssessmentResponseDetailsPage),
    },
    // Survey results: a class's student survey and one survey's, their written answers, and one response (LCH-22).
    {
      path: classSurveyResultsRoute,
      titleId: 'launch.surveyResults.title',
      page: () => import('./surveys/SurveyResultsPage').then((module) => module.ClassSurveyResultsPage),
    },
    {
      path: surveyResultsRoute,
      titleId: 'launch.surveyResults.title',
      page: () => import('./surveys/SurveyResultsPage').then((module) => module.SurveyResultsPage),
    },
    {
      path: classTextAnswersRoute,
      titleId: 'launch.surveyResults.textAnswers.title',
      page: () => import('./surveys/TextAnswersPage').then((module) => module.ClassTextAnswersPage),
    },
    {
      path: surveyTextAnswersRoute,
      titleId: 'launch.surveyResults.textAnswers.title',
      page: () => import('./surveys/TextAnswersPage').then((module) => module.SurveyTextAnswersPage),
    },
    {
      path: surveyResponseRoute,
      titleId: 'launch.surveyResults.response.eyebrow',
      page: () => import('./surveys/SurveyResponseDetailsPage').then((module) => module.SurveyResponseDetailsPage),
    },
  ],
  // No top nav item: administrators find responses from Admin Home, instructors from the Instructor Dashboard (112100).
  adminHome: {
    // Admin Home declares the LTI category, in the mockups' place under Learning Content.
    links: [
      { id: 'find-lti-tools', category: 'lti-tools', labelId: 'launch.lti.find.title', path: ltiToolsPath, permission: readLtiToolsPermission },
      { id: 'create-lti-tool', category: 'lti-tools', labelId: 'launch.lti.create.title', path: createLtiToolPath, permission: writeLtiToolsPermission },
      {
        id: 'find-lab-on-demand-connections',
        category: 'labs',
        labelId: 'launch.labOnDemand.find.title',
        path: connectionsPath,
        permission: labOnDemandRead,
      },
      { id: 'find-lab-instances', category: 'labs', labelId: 'launch.labInstances.find.title', path: labInstancesPath, permission: readLabInstancesPermission },
      {
        id: 'create-lab-on-demand-connection',
        category: 'labs',
        labelId: 'launch.labOnDemand.create.title',
        path: createConnectionPath,
        // Every site launches labs through a connection, so only a Super User creates one (112310).
        isSuperUserOnly: true,
      },
      { id: 'find-launches', category: 'courses', labelId: 'launch.launches.find.title', path: launchesPath, permission: readLaunchesPermission },
      {
        id: 'find-assessment-responses',
        category: 'assessments',
        labelId: 'launch.assessmentResponses.find.title',
        path: assessmentResponsesPath,
        permission: readAssessmentResponsesPermission,
      },
    ],
  },
  slots: [
    // Launch's part of "Download my data" on My Profile (LCH-32).
    { kind: 'link', slot: myDataSlot, id: 'launch.my-data', labelId: 'launch.myData.part', path: myDataApi },
    {
      // Find Responses on Assessment Details, the mockups' quick-launch link (LCH-20).
      kind: 'link',
      slot: detailsSlots(assessmentDetailsSlot).menu,
      id: 'find-responses',
      labelId: 'launch.assessmentResponses.findResponses',
      icon: FileSpreadsheet,
      permission: readAssessmentResponsesPermission,
      path: (context) => assessmentResponsesFor(context.recordId ?? ''),
    },
    {
      // The tool picker in Catalog's LTI link editor (CAT-10's slot).
      kind: 'panel',
      slot: ltiToolPickerSlot,
      id: 'lti-tool-picker',
      component: () => import('./lti/LtiToolPicker').then((module) => module.LtiToolPicker),
    },
    {
      // Add from Tool among the Add tiles of Catalog's activity editor: LTI deep linking (LCH-18).
      kind: 'panel',
      slot: activityAddSlot,
      id: 'lti-deep-link',
      component: () => import('./lti/AddFromTool').then((module) => module.AddFromTool),
    },
    {
      // What came of Add from Tool, above the nav bar of its course editor, kept across the reload that shows the new activities.
      kind: 'panel',
      slot: shellSlots.headerBanners,
      id: 'lti-deep-link-return',
      component: () => import('./lti/DeepLinkReturnNotice').then((module) => module.DeepLinkReturnNotice),
    },
    {
      // The `launch` settings group on Create and Edit Organization (LCH-28).
      kind: 'tab',
      slot: organizationFormSectionsSlot,
      id: 'launch',
      labelId: 'launch.settings.label',
      component: () => import('./settings/LaunchSettingsSection').then((module) => module.LaunchSettingsSection),
    },
    {
      // The organization's Lab on Demand mappings, a tab on Organization Details (LCH-10, SM-08's slot).
      kind: 'tab',
      slot: detailsSlots(organizationDetailsSlot).tabs,
      id: 'lab-on-demand',
      labelId: 'launch.labOnDemand.organizationMappings.tab',
      component: () => import('./lab-on-demand/OrganizationLabOnDemandTab').then((module) => module.OrganizationLabOnDemandTab),
    },
    {
      // The class's surveys the viewer may answer now, in Class Details' header (LCH-21, story 111702).
      kind: 'action',
      slot: detailsSlots(classDetailsSlot).actions,
      id: 'class-surveys',
      component: () => import('./surveys/ClassSurveyActions').then((module) => module.ClassSurveyActions),
    },
    {
      // The Microsoft Learn buttons on the learner class page (LCH-23, CLS-05's slot).
      kind: 'panel',
      slot: detailsSlots(classSlots.learnerPage).panels,
      id: 'microsoft-learn',
      component: () => import('./microsoft-learn/MicrosoftLearnPanel').then((module) => module.MicrosoftLearnPanel),
    },
    // Class Details' Monitor Labs (LCH-09, CLS-01's slot), first in the mockup's Labs group.
    {
      kind: 'link',
      slot: classSlots.detailsSidebar,
      id: 'lab-monitor',
      labelId: 'launch.labInstances.monitor.title',
      icon: Activity,
      permission: writeLabInstancesPermission,
      path: (context) => labMonitorPath(context.recordId ?? ''),
    },
    // Class Details' Find Lab Instances and Exam Lab Instances (LCH-08, CLS-01's slot), and the instructor dashboard's Lab Instances quick link.
    {
      kind: 'link',
      slot: classSlots.detailsSidebar,
      id: 'lab-instances',
      labelId: 'launch.labInstances.find.title',
      icon: FlaskConical,
      permission: readLabInstancesPermission,
      path: (context) => classLabInstancesPath(context.recordId ?? ''),
    },
    {
      kind: 'link',
      slot: classSlots.detailsSidebar,
      id: 'exam-lab-instances',
      labelId: 'launch.labInstances.examInstances',
      icon: ClipboardList,
      permission: readLabInstancesPermission,
      path: (context) => classExamLabInstancesPath(context.recordId ?? ''),
    },
    // Class Details' Survey Results and its two exports (LCH-22), which the results page starts.
    {
      kind: 'link',
      slot: classSlots.detailsSidebar,
      id: 'survey-results',
      labelId: 'launch.surveyResults.title',
      icon: ClipboardCheck,
      permission: readSurveyResponsesPermission,
      path: (context) => resultsPath({ kind: 'class', id: context.recordId ?? '' }),
    },
    {
      kind: 'link',
      slot: classSlots.detailsSidebar,
      id: 'export-survey-responses',
      labelId: 'launch.surveyResults.exportNamed',
      icon: Download,
      permission: readRespondentsPermission,
      path: (context) => resultsPath({ kind: 'class', id: context.recordId ?? '' }, 'names'),
    },
    {
      kind: 'link',
      slot: classSlots.detailsSidebar,
      id: 'export-anonymous-survey-responses',
      labelId: 'launch.surveyResults.exportAnonymous',
      icon: Download,
      permission: readSurveyResponsesPermission,
      path: (context) => resultsPath({ kind: 'class', id: context.recordId ?? '' }, 'anonymous'),
    },
    {
      // Survey Details' toolbar: Survey Responses and both exports (LCH-22, CAT-09's slot).
      kind: 'action',
      slot: detailsSlots(surveyDetailsSlot).actions,
      id: 'survey-results',
      component: () => import('./surveys/SurveyResultsActions').then((module) => module.SurveyResultsActions),
    },
    { kind: 'link', slot: classSlots.dashboardLinks, id: 'lab-instances', labelId: 'launch.labInstances.find.title', path: labInstancesPath, permission: readLabInstancesPermission },
    {
      // The Instructor Dashboard's way to the responses of the viewer's classes (112100).
      kind: 'link',
      slot: classSlots.dashboardLinks,
      id: 'assessment-responses',
      labelId: 'launch.assessmentResponses.find.title',
      path: assessmentResponsesPath,
      permission: readAssessmentResponsesPermission,
    },
    // The activity list (LCH-03): Enrollment Details, Course Assignment Details, the student view of Course Details, and Class Details' rows for instructors.
    {
      kind: 'panel',
      slot: enrollmentActivitiesSlot,
      id: 'activity-list',
      component: () => import('./activity-list/ActivityListPanels').then((module) => module.EnrollmentActivities),
    },
    {
      kind: 'panel',
      slot: courseAssignmentActivitiesSlot,
      id: 'activity-list',
      component: () => import('./activity-list/ActivityListPanels').then((module) => module.CourseAssignmentActivities),
    },
    {
      kind: 'panel',
      slot: courseCatalogSlots.activities,
      id: 'activity-list',
      component: () => import('./activity-list/ActivityListPanels').then((module) => module.LearnerCourseActivities),
    },
    {
      kind: 'action',
      slot: classSlots.activityLaunch,
      id: 'launch',
      component: () => import('./activity-list/ActivityListPanels').then((module) => module.ClassActivityLaunch),
    },
    // Each student's percent complete (LCH-29): a column on Class Roster, whose cells read their page once, and the instructor dashboard's sessions.
    column<ClassProgressCellValue>({
      kind: 'column',
      slot: classSlots.rosterColumns,
      id: 'percent-complete',
      headerId: 'launch.classProgress.column',
      // With no class, each cell shows a dash and nothing is asked.
      load: (ids, context) => {
        const { recordId } = context
        return Promise.resolve(recordId === undefined || recordId === '' ? new Map() : new Map(ids.map((id) => [id, { classId: recordId, page: ids }])))
      },
      cell: () => import('./class-progress/ClassProgressCell').then((module) => module.ClassProgressCell),
    }),
    column<number>({
      kind: 'column',
      slot: classSlots.dashboardProgress,
      id: 'percent-complete',
      headerId: 'launch.classProgress.column',
      permission: 'launch.launches.read',
      load: async (ids, context, client) => {
        if (client === undefined || context.recordId === undefined || context.recordId === '') {
          throw new Error('The class and an API client are needed to read progress.')
        }
        const { loadDashboardProgress } = await import('./class-progress/class-progress')
        return loadDashboardProgress(client, context.recordId, ids)
      },
      cell: () => Promise.resolve(() => null),
    }),
    {
      // Course Resources the viewer may open, on the student view of Course Details (LCH-27).
      kind: 'panel',
      slot: courseCatalogSlots.resources,
      id: 'course-files',
      component: () => import('./course-files/CourseFilesPanel').then((module) => module.LearnerCourseFiles),
    },
    // My labs (LCH-07): on the Student Dashboard, and beside the activity list on Enrollment Details and Course Assignment Details.
    {
      kind: 'panel',
      slot: studentDashboardSlots.main,
      id: 'my-labs',
      component: () => import('./my-labs/MyLabsPanel').then((module) => module.DashboardMyLabs),
    },
    {
      kind: 'panel',
      slot: enrollmentActivitiesSlot,
      id: 'my-labs',
      component: () => import('./my-labs/MyLabsPanel').then((module) => module.EnrollmentMyLabs),
    },
    {
      kind: 'panel',
      slot: courseAssignmentActivitiesSlot,
      id: 'my-labs',
      component: () => import('./my-labs/MyLabsPanel').then((module) => module.CourseAssignmentMyLabs),
    },
  ],
}
